import { Alert, Button, Snackbar, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo } from "react";
import { MapContainer, ScaleControl, ZoomControl } from "react-leaflet";
import useStore, { MapLayer } from "../utils/store";

import GeoJSONLayer from "../components/GeoJsonLayer";
import "../scss/Dashboard.scss";
import NavToolbar from "../components/NavToolbar";
import CurrentJobChip from "../components/CurrentJobChip";
import CustomDownloadModal from "../components/CustomDownloadModal";
import JobQueue from "../components/JobQueue";
import MultiGeoJSONLayer from "../components/MultiGeoJsonLayer";
import LayersControl from "../components/LayersControl";
import { useAuth0 } from "@auth0/auth0-react";
import LoginButton from "../components/LoginButton";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import UsersList from "../components/UsersList";
import MapLayersPanel from "../components/MapLayersPanel";
import {
  MAP_LAYER_OPTIONS,
  ET_COLORMAP,
  DIFF_COLORMAP,
  REFERENCE_GEOJSONS,
  ARD_TILES_DATA_VERSION,
  getCdlDisplayYear,
} from "../utils/constants";
import { formatCdlLegendTitle } from "../utils/cdlYear";
import ActiveMapLayer from "../components/ActiveMapLayer";
import { CRS } from "leaflet";
import DrawControls from "../components/DrawControls";
import PixelDrawControl from "../components/PixelDrawControl";
import MapSearchControl from "../components/MapSearchControl";
import ActiveMonthlyMapLayer from "../components/ActiveMonthlyMapLayer";
import GroupPreviewMapLayer from "../components/GroupPreviewMapLayer";
import ColorScale from "../components/ColorScale";
import { CdlHoverIdentify, CdlLegendFab } from "../components/CdlMapExtras";
import useCurrentJobStore from "../utils/currentJobStore";
import dayjs from "dayjs";
import { enableNotificationPermissionOnUserGesture, initBrowserNotificationServiceWorker, isBrowserNotificationSupported, JOB_COMPLETION_NOTIFICATION_EVENT } from "../utils/browserNotifications";

const QueuePollingEffect = memo(() => {
  const pollCount = useStore((s) => s.pollCount);
  const fetchQueue = useStore((s) => s.fetchQueue);
  const { isAuthenticated, user } = useAuth0();

  useEffect(() => {
    fetchQueue();
  }, [pollCount, isAuthenticated, user?.email_verified, fetchQueue]);

  return null;
});

const BrowserNotificationPermissionEffect = memo(() => {
  const userInfo = useStore((state) => state.userInfo);
  const fetchQueue = useStore((state) => state.fetchQueue);
  const canReadJobs = useMemo(() => userInfo?.permissions.includes("read:jobs"), [userInfo?.permissions]);

  useEffect(() => {
    if (!canReadJobs || !isBrowserNotificationSupported()) {
      return;
    }

    void initBrowserNotificationServiceWorker();
    enableNotificationPermissionOnUserGesture();
  }, [canReadJobs]);

  useEffect(() => {
    const handleJobNotification = (event: Event) => {
      const detail = (event as CustomEvent<{ title?: string; body?: string }>).detail;
      if (!detail?.title) {
        return;
      }

      const message = detail.body ? `${detail.title} — ${detail.body}` : detail.title;
      useStore.getState().setSuccessMessage(message);
    };

    window.addEventListener(JOB_COMPLETION_NOTIFICATION_EVENT, handleJobNotification);
    return () => window.removeEventListener(JOB_COMPLETION_NOTIFICATION_EVENT, handleJobNotification);
  }, []);

  useEffect(() => {
    if (!canReadJobs) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchQueue();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [canReadJobs, fetchQueue]);

  return null;
});

const Dashboard = () => {
  const loadedGeoJSON = useStore((state) => state.loadedGeoJSON);
  const multipolygons = useStore((state) => state.multipolygons);
  const locations = useStore((state) => state.locations);
  const showUploadDialog = useStore((state) => state.showUploadDialog);
  const [successMessage, setSuccessMessage] = useStore((state) => [state.successMessage, state.setSuccessMessage]);
  const [errorMessage, setErrorMessage] = useStore((state) => [state.errorMessage, state.setErrorMessage]);

  const setIsQueueOpen = useStore((state) => state.setIsQueueOpen);
  const setIsBacklogOpen = useStore((state) => state.setIsBacklogOpen);
  const setIsUsersPanelOpen = useStore((state) => state.setIsUsersPanelOpen);
  const setIsMapLayersPanelOpen = useStore((state) => state.setIsMapLayersPanelOpen);
  const isRightPanelOpen = useStore((state) => state.isRightPanelOpen);

  const reverifyEmail = useStore((state) => state.reverifyEmail);

  const { isAuthenticated, user, loginWithRedirect } = useAuth0();
  const userInfo = useStore((state) => state.userInfo);
  const canReadJobs = useMemo(() => userInfo?.permissions.includes("read:jobs"), [userInfo?.permissions]);
  const isAdmin = useMemo(() => userInfo?.permissions.includes("write:admin"), [userInfo?.permissions]);
  const fetchARDTiles = useStore((state) => state.fetchARDTiles);
  const showARDTiles = useStore((state) => state.showARDTiles);
  const ardTiles = useStore((state) => state.ardTiles);
  const ardTilesDataVersion = useStore((state) => state.ardTilesDataVersion);
  const fetchDroughtMonitorData = useStore((state) => state.fetchDroughtMonitorData);
  const droughtMonitorData = useStore((state) => state.droughtMonitorData);

  const activeJob = useStore((state) => state.activeJob);
  const activeJobGroup = useStore((state) => state.activeJobGroup);
  const setActiveJob = useStore((state) => state.setActiveJob);

  const setLoadedGeoJSON = useStore((state) => state.setLoadedGeoJSON);
  const visibleReferenceLayers = useStore((state) => state.visibleReferenceLayers);

  const closeNewJob = useStore((state) => state.closeNewJob);

  const showAllCompletedJobs = useStore((state) => state.showAllCompletedJobs);
  const allGeoJSONs = useStore((state) => state.allGeoJSONs);

  const visibleReferenceGeoJSONs = useMemo(() => {
    if (!visibleReferenceLayers || !REFERENCE_GEOJSONS) {
      return [];
    }

    return visibleReferenceLayers.map(
      (layer) => (REFERENCE_GEOJSONS as Record<string, { name: string; data?: any; droughtMonitor?: boolean }>)?.[layer]
    );
  }, [visibleReferenceLayers]);

  useEffect(() => {
    if (visibleReferenceGeoJSONs.some((layer) => layer.droughtMonitor)) {
      fetchDroughtMonitorData();
    }
  }, [fetchDroughtMonitorData, visibleReferenceGeoJSONs]);

  const mapLayerKey = useStore((state) => state.mapLayerKey);
  const tileDate = useStore((state) => state.tileDate);

  const [minBaseColor, maxBaseColor] = useStore((state) => [state.minimumBaseMapColorBound, state.maximumBaseMapColorBound]);
  const refreshType = useStore((state) => state.refreshType);
  const comparisonMode = useStore((state) => state.comparisonMode);

  const showPreview = useCurrentJobStore((state) => state.showPreview);
  const previewMin = useCurrentJobStore((state) => state.previewMin);
  const previewMax = useCurrentJobStore((state) => state.previewMax);
  const jobLocateGeneration = useStore((state) => state.jobLocateGeneration);

  const minColor = useMemo(() => {
    if (refreshType === "static") {
      return 0;
    } else {
      return minBaseColor || 0;
    }
  }, [minBaseColor, refreshType]);

  const maxColor = useMemo(() => {
    if (refreshType === "static") {
      return 200;
    } else {
      return maxBaseColor || 200;
    }
  }, [maxBaseColor, refreshType]);

  const colorScale = useMemo(() => {
    return comparisonMode === "absolute" ? ET_COLORMAP : DIFF_COLORMAP;
  }, [comparisonMode]);

  const activeMapLayer = useMemo(() => {
    let mapLayer = (MAP_LAYER_OPTIONS as any)?.[mapLayerKey] as MapLayer;
    if (!mapLayer) {
      mapLayer = MAP_LAYER_OPTIONS["Google Satellite"];
    }

    const layer = JSON.parse(JSON.stringify(mapLayer));
    if (tileDate) {
      layer.url = layer.url.replace("{time}", tileDate);
    } else if (layer.time) {
      layer.url = layer.url.replace("{time}", layer.time);
    }

    return layer;
  }, [mapLayerKey, tileDate]);

  const showColorScale = useMemo(() => {
    return !!activeMapLayer?.showColorScale;
  }, [activeMapLayer?.showColorScale]);

  const showWmsLegend = useMemo(
    () => !!(activeMapLayer?.wmsLegend && activeMapLayer?.wmsLayers),
    [activeMapLayer?.wmsLegend, activeMapLayer?.wmsLayers]
  );

  const authToken = useStore((s) => s.authToken);
  const cdlReleaseYear = useStore((s) => s.cdlReleaseYear);
  const fetchCdlReleaseYearIfNeeded = useStore((s) => s.fetchCdlReleaseYearIfNeeded);

  useEffect(() => {
    if (!authToken) {
      return;
    }
    fetchCdlReleaseYearIfNeeded();
  }, [authToken, fetchCdlReleaseYearIfNeeded]);

  const cdlLegendTitle = useMemo(() => {
    const year = getCdlDisplayYear(activeMapLayer, cdlReleaseYear);
    return formatCdlLegendTitle(year);
  }, [activeMapLayer, cdlReleaseYear]);

  const showPreviewColorScale = useMemo(() => {
    if (!showPreview || previewMin == null || previewMax == null || previewMin === previewMax) {
      return false;
    }
    return true;
  }, [showPreview, previewMin, previewMax]);

  const cdlLegendRightPx = useMemo(() => {
    if (!showPreviewColorScale) {
      return isRightPanelOpen ? 350 : 50;
    }
    return isRightPanelOpen ? 384 : 100;
  }, [isRightPanelOpen, showPreviewColorScale]);

  const showRightMapLegend = showColorScale || showWmsLegend;

  useEffect(() => {
    const needsFetch =
      showARDTiles &&
      (!Object.keys(ardTiles).length || ardTilesDataVersion !== ARD_TILES_DATA_VERSION);

    if (needsFetch) {
      fetchARDTiles();
    }
  }, [showARDTiles, ardTiles, ardTilesDataVersion, fetchARDTiles]);

  const loadVersion = useStore((state) => state.loadVersion);

  const handleKeyPress = useCallback(
    (event: any) => {
      const isMac = navigator.userAgent.includes("Mac");
      const isCmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

      if (isCmdOrCtrl && event.key === "b") {
        event.preventDefault();
        if (isRightPanelOpen) {
          setIsBacklogOpen(false);
          setIsQueueOpen(false);
          setIsUsersPanelOpen(false);
          setIsMapLayersPanelOpen(false);
        } else {
          setIsBacklogOpen(false);
          setIsUsersPanelOpen(false);
          setIsMapLayersPanelOpen(false);
          setIsQueueOpen(true);
        }
      }
    },
    [isRightPanelOpen, setIsBacklogOpen, setIsQueueOpen, setIsUsersPanelOpen, setIsMapLayersPanelOpen]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  // Poll for email verification
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && !user?.email_verified) {
        loginWithRedirect();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.email_verified]);

  // Poll for permissions
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && user?.email_verified && !canReadJobs) {
        loginWithRedirect();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.email_verified, canReadJobs]);

  useEffect(() => {
    loadVersion();

    const interval = setInterval(() => {
      useStore.getState().increasePollCount();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isRightPanelOpen) {
      const controls = document.querySelectorAll(".leaflet-right .leaflet-control");
      controls.forEach((control) => {
        if (control instanceof HTMLElement && control) {
          control.style.marginRight = "310px";

          const drawActions = document.querySelectorAll(".leaflet-touch .leaflet-right .leaflet-draw-actions");
          drawActions.forEach((action) => {
            if (action instanceof HTMLElement && action) {
              action.style.right = showRightMapLegend ? "78px" : "38px";
            }
          });
        }
      });
    } else {
      const controls = document.querySelectorAll(".leaflet-right .leaflet-control");
      controls.forEach((control) => {
        if (control instanceof HTMLElement && control) {
          control.style.marginRight = "10px";

          const drawActions = document.querySelectorAll(".leaflet-touch .leaflet-right .leaflet-draw-actions");
          drawActions.forEach((action) => {
            if (action instanceof HTMLElement && action) {
              action.style.right = showRightMapLegend ? "78px" : "38px";
            }
          });
        }
      });
    }
  }, [isRightPanelOpen, showRightMapLegend]);

  const loadedGeoJSONTooltipText = useMemo(() => {
    if (!activeJob) {
      return "";
    }

    let dateTooltip = "";
    if (activeJob?.status === "Complete") {
      dateTooltip = `Completed: ${dayjs(activeJob?.ended).format("MM/DD/YYYY")}`;
    } else if (activeJob?.started) {
      dateTooltip = `Started: ${dayjs(activeJob?.started).format("MM/DD/YYYY")}`;
    } else {
      dateTooltip = `Submitted: ${dayjs(activeJob.submitted).format("MM/DD/YYYY")}`;
    }

    return `Name: ${activeJob?.name}\n${dateTooltip}\nStatus: ${activeJob?.status}\nRequested by: ${activeJob?.user?.name}`;
  }, [activeJob]);

  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative", overflow: "hidden" }}>
      <QueuePollingEffect />
      <BrowserNotificationPermissionEffect />
      <NavToolbar />
      {isAuthenticated && <CustomDownloadModal />}
      {isAuthenticated && activeJob && (activeJobGroup || multipolygons.length <= 1) && <CurrentJobChip />}
      {isAuthenticated && showUploadDialog && <LayersControl />}
      {(!isAuthenticated || (!canReadJobs && userInfo)) && (
        <div
          style={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            background: "rgb(0 0 0 / 50%)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "var(--st-gray-90)",
              padding: "16px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              width: "600px",
              gap: "8px",
            }}
          >
            <Typography variant="h4" style={{ lineHeight: "8px", textAlign: "center" }}>
              <WaterDropIcon style={{ color: "var(--st-gray-30)", fontSize: "60px" }} />
            </Typography>
            <Typography
              variant="h4"
              style={{
                color: "white",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              New Mexico ET Reporting Tool
            </Typography>
            <Typography
              variant="h4"
              style={{
                fontSize: "16px",
                color: "var(--st-gray-30)",
                textAlign: "center",
                marginBottom: "32px",
                maxWidth: "600px",
                lineHeight: "1.6"
              }}
            >
              {!isAuthenticated ? (
                <>
                  This tool is administered by the New Mexico Office of the State Engineer.
                  <br />
                  <br />
                  Please login or sign up for an account to continue.
                  <br />
                  <br />
                  If you are not a current agency employee and would like to request access or have any questions,
                  please contact the OSE's Water Use and Conservation Bureau at{" "}
                  <a
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=water.nm@ose.nm.gov&su=ET%20Tool%20Access%20Request"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "var(--st-blue-40)",
                      textDecoration: "none",
                      borderBottom: "1px solid var(--st-blue-40)",
                      cursor: "pointer",
                      transition: "color 0.2s ease"
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.color = "var(--st-blue-30)")}
                    onMouseOut={(e) => (e.currentTarget.style.color = "var(--st-blue-40)")}
                    title="Click to compose email in Gmail"
                  >
                    water.nm@ose.nm.gov
                  </a>.
                </>
              ) : user?.email_verified ? (
                "Thanks for creating an account. Your email address has been verified. NM OSE staff will review your request for access shortly."
              ) : (
                "Please verify your email address and then log out and back in to continue."
              )}
            </Typography>
            {isAuthenticated && !user?.email_verified && (
              <Button
                variant="contained"
                onClick={() => {
                  if (user?.sub) {
                    reverifyEmail(user?.sub);
                  }
                }}
              >
                Resend Email
              </Button>
            )}
            {!isAuthenticated && <LoginButton title="Login/Sign Up" />}
          </div>
        </div>
      )}
      <JobQueue />
      {<MapLayersPanel />}
      {isAdmin && <UsersList />}
      <div
        style={{
          position: "relative",
          width: "100vw",
          height: "calc(100vh - 45px)",
        }}
      >
        <MapContainer
          className="map-container"
          style={{ width: "100%", height: "100%" }}
          center={[34.5199, -105.8701]}
          zoom={7}
          zoomControl={false}
          scrollWheelZoom={true}
          maxZoom={activeMapLayer.maxZoom}
          crs={CRS.EPSG3857}
        >
          {!activeJobGroup && <ActiveMonthlyMapLayer />}
          <GroupPreviewMapLayer />
          <ActiveMapLayer />
          {showWmsLegend && <CdlHoverIdentify />}
          <ZoomControl position="topright" />
          <DrawControls />
          <PixelDrawControl />
          <MapSearchControl />
          <ScaleControl position="bottomleft" />

          {showColorScale && (
            <ColorScale
              label={`${activeMapLayer?.name} (${tileDate
                ? new Date(tileDate).toLocaleString("default", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
                : new Date(activeMapLayer?.time).toLocaleString("default", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
                })`}
              maxValue={maxColor}
              minValue={minColor}
              colorScale={colorScale}
              style={{ right: isRightPanelOpen ? "350px" : "50px", transition: "right 0.1s ease" }}
            />
          )}
          {ardTiles && showARDTiles && <GeoJSONLayer data={ardTiles} validateBounds={false} fitToBounds={false} />}
          {showAllCompletedJobs &&
            allGeoJSONs &&
            allGeoJSONs.length > 0 &&
            allGeoJSONs.map((geojson) => {
              if (!geojson?.geojson) {
                return null;
              }

              let dateTooltip = "";
              if (geojson.status === "Complete") {
                dateTooltip = `Completed: ${dayjs(geojson.ended).format("MM/DD/YYYY")}`;
              } else if (geojson.started) {
                dateTooltip = `Started: ${dayjs(geojson.started).format("MM/DD/YYYY")}`;
              } else {
                dateTooltip = `Submitted: ${dayjs(geojson.submitted).format("MM/DD/YYYY")}`;
              }

              const tooltipText = `Name: ${geojson?.name || "N/A"}\n${dateTooltip}\nStatus: ${geojson?.status || "N/A"
                }\nRequested by: ${geojson?.user?.name || "Unknown"}`;

              return (
                <GeoJSONLayer
                  key={geojson.key}
                  data={geojson.geojson}
                  validateBounds={false}
                  fitToBounds={false}
                  showLabels={true}
                  showAreaLabel={true}
                  tooltipText={tooltipText}
                  onSelect={() => {
                    const newJob = { ...geojson };
                    newJob.loaded_geo_json = geojson.geojson;
                    setActiveJob(newJob);
                    setLoadedGeoJSON(newJob.loaded_geo_json);
                    closeNewJob();
                  }}
                />
              );
            })}
          {visibleReferenceGeoJSONs.map((layer) => (
            <GeoJSONLayer
              key={layer.name}
              data={layer.droughtMonitor ? droughtMonitorData : layer.data}
              isDroughtMonitor={layer.droughtMonitor}
              validateBounds={false}
              fitToBounds={false}
              showLabels={true}
            />
          ))}
          <GeoJSONLayer
            data={loadedGeoJSON}
            tooltipText={loadedGeoJSONTooltipText}
            showLabels={!showPreview}
            showAreaLabel={!showPreview}
            outline={showPreview}
            fitToBounds={!showPreview}
            locateGeneration={jobLocateGeneration}
          />
          <MultiGeoJSONLayer data={multipolygons} locations={locations} />
        </MapContainer>
        {showWmsLegend && (
          <CdlLegendFab title={cdlLegendTitle} rightPx={cdlLegendRightPx} />
        )}
      </div>
      <Snackbar
        open={successMessage.length > 0}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={errorMessage.length > 0} autoHideDuration={5000} onClose={() => setErrorMessage("")}>
        <Alert onClose={() => setErrorMessage("")} severity="error" variant="filled" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Dashboard;
